import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  checkIfInGlobalBlockList,
  getCustomSchemas,
  getGmailSendAsForSignatures,
  getSignatures,
  getUserQuery,
} from '../../../../../api/users-api';
import SignaturesForm from './signatures-form';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import { SignatureModel } from '../../../../../../types';
import { Messages } from '../../../../../utils/message-constants';

import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

export default function SignaturesPage() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();
  const { data: user } = useQuery(getUserQuery(userEmail));

  const [queryData] = useState<{
    cursor: string | null;
    results: SignatureModel[];
    nameSpace: string;
    firstPull: boolean;
    searchTime: number;
    pageSize: number;
  }>({
    cursor: null,
    results: [],
    nameSpace: '',
    firstPull: true,
    searchTime: 0,
    pageSize: 100,
  });

  const {
    data: sendAsAccounts,
    isLoading: sendAsAccountsInitialLoading,
    isFetching: sendAsAccountsFetching,
  } = useQuery({
    queryKey: [UserKeys.SendAsAccounts, userEmail],
    queryFn: () => getGmailSendAsForSignatures(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading(
        "this user's send as accounts"
      ),
    },
  });

  const {
    data: signatures,
    isLoading: signaturesLoading,
    isFetching: signaturesFetching,
  } = useQuery({
    queryKey: [UserKeys.Signatures, queryData, userEmail],
    queryFn: () => getSignatures(queryData),
    meta: {
      errorMessage: Messages.Errors.Network.Loading(`this user's signatures`),
    },
  });

  const {
    data: isUserExcluded,
    isLoading: checkBlockLoading,
    isFetching: checkBlockFetching,
  } = useQuery({
    queryKey: [UserKeys.CheckIfInGlobalBlockList, userEmail],
    queryFn: () => checkIfInGlobalBlockList(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('the gPanel block list'),
    },
  });

  const {
    data: customSchemas,
    isLoading: isLoadingCustomSchemas,
    isFetching: isFetchingCustomSchemas,
  } = useQuery({
    queryKey: [UserKeys.CustomSchemas, userEmail],
    queryFn: () => getCustomSchemas(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading(
        "this user's custom attributes"
      ),
    },
  });

  if (
    sendAsAccountsInitialLoading ||
    sendAsAccountsFetching ||
    signaturesLoading ||
    signaturesFetching ||
    checkBlockLoading ||
    checkBlockFetching ||
    isLoadingCustomSchemas ||
    isFetchingCustomSchemas
  ) {
    return (
      <Card
        sx={{
          height: '100%',
        }}
      >
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ width: '100%', minHeight: '100%' }}
        >
          <CircularProgress />
        </Stack>
      </Card>
    );
  }

  if (
    sendAsAccounts &&
    signatures &&
    isUserExcluded !== undefined &&
    customSchemas &&
    user
  ) {
    return (
      <SignaturesForm
        userEmail={userEmail}
        userRoles={userRoles}
        sendAsAccounts={sendAsAccounts}
        signatures={signatures.results}
        isUserExcluded={isUserExcluded}
        user={user}
        customSchemas={customSchemas}
        changeLogRoles={changeLogRoles}
      />
    );
  }
}
